const DB_VERSION = 19;

const BATCH_SIZE = 100;

export const PREFIX = 'treams-analytics';
export const SYNC = 'indexeddb-sync';
export const GROUPS = 'analytics-selectable-groups';

// Keyed by user as well as workspace: multiple accounts can be signed in on one
// device via session slots, and each gets a permission-scoped snapshot.
export function analyticsDatabaseName(workspaceAlias: string, userID: string): string {
	return `${PREFIX}:${workspaceAlias}:${userID}`;
}

type Config = {
	id?: string;
	dates?: string[];
	indexes?: string[];
	parent?: { store: string; id: string };
};

// Keep in sync with the backend INDEXED_DB list
// (backend/lambdas/analytics-v3/src/engine/config.ts): stores missing here are
// silently discarded during ingest.
export const CONFIG: Record<string, Config> = {
	Users: {
		id: 'userID',
		indexes: ['createdAt'],
		dates: [
			'_employment_ended',
			'createdAt',
			'updatedAt',
			'activatedAt',
			'deactivatedAt',
			'archivedAt',
			'lastLoginAt',
			'localActivatedAt',
			'localDeactivatedAt',
		],
	},
	Groups: {
		id: 'groupID',
		indexes: ['createdAt'],
		dates: ['createdAt', 'updatedAt'],
	},
	EvaluationSubmissions: {
		id: 'evaluationSubmissionID',
		indexes: ['createdAt', 'deadline'],
		dates: [
			'_employment_ended',
			'createdAt',
			'updatedAt',
			'archivedAt',
			'deadline',
			'employeeSignedAt',
			'managerSignedAt',
		],
	},
	EvaluationFormItems: {
		id: 'evaluationFormItemID',
		indexes: ['createdAt'],
		dates: ['createdAt', 'updatedAt', 'archivedAt'],
	},
	CycleTemplates: {
		id: 'cycleTemplateID',
		indexes: ['createdAt'],
		dates: ['createdAt', 'updatedAt', 'archivedAt'],
	},
	CycleInstances: {
		id: 'cycleInstanceID',
		indexes: ['createdAt'],
		dates: [
			'_employment_ended',
			'createdAt',
			'updatedAt',
			'activatesAt',
			'archivedAt',
			'startDate',
			'localStartDate',
		],
	},
	MomentTemplates: {
		id: 'momentTemplateID',
		indexes: ['createdAt'],
		dates: ['createdAt', 'updatedAt', 'archivedAt'],
	},
	MomentInstances: {
		id: 'momentInstanceID',
		indexes: ['createdAt'],
		dates: [
			'_employment_ended',
			'createdAt',
			'updatedAt',
			'completedAt',
			'startDate',
			'endDate',
			'localStartDate',
			'localEndDate',
		],
	},
	Goals: {
		id: 'goalID',
		indexes: ['createdAt'],
		dates: ['_employment_ended', 'createdAt', 'updatedAt', 'completedAt', 'archivedAt', 'dueDate'],
	},
	FeedbackResponses: {
		id: 'responseID',
		indexes: ['createdAt'],
		dates: ['_employment_ended', 'createdAt', 'updatedAt', 'archivedAt'],
	},
	Comments: {
		id: 'commentID',
		indexes: ['createdAt'],
		dates: ['_employment_ended', 'createdAt'],
	},
	Compliments: {
		id: 'complimentID',
		indexes: ['createdAt'],
		dates: ['_employment_ended', 'createdAt', 'archivedAt'],
	},
	Notes: {
		id: 'noteID',
		indexes: ['createdAt'],
		dates: ['_employment_ended', 'createdAt'],
	},
	FeedbackRequests: {
		id: 'requestID',
		indexes: ['createdAt'],
		dates: ['_employment_ended', 'createdAt', 'dueDate'],
	},
	Meetings: {
		id: 'targetTypeID',
		indexes: ['startsAt'],
		dates: ['startsAt'],
	},
	TalentReviewPlacements: {
		id: 'placementID',
		indexes: ['createdAt'],
		dates: ['_employment_ended', 'createdAt'],
	},
	EngagementAnswers: {
		id: 'answerID',
		indexes: ['createdAt'],
		dates: ['createdAt'],
	},
	EngagementResponses: {
		id: 'responseID',
		indexes: ['createdAt'],
		dates: ['createdAt'],
	},
	EngagementRequests: {
		id: 'requestID',
		indexes: ['createdAt'],
		dates: ['createdAt'],
	},
	EngagementTemplates: {
		id: 'templateID',
		indexes: ['createdAt'],
		dates: ['createdAt'],
	},
	SalaryChanges: {
		id: 'salaryChangeID',
		indexes: ['createdAt'],
		dates: ['createdAt'],
	},
	JobPositions: {
		id: 'jobPositionID',
		indexes: ['startDate'],
		dates: ['startDate', 'endDate'],
	},
	JobTitles: {
		id: 'jobTitleID',
		indexes: ['createdAt'],
	},
	SalaryScales: {
		id: 'salaryScaleID',
		indexes: ['createdAt'],
	},
};

export async function openDatabase(name: string): Promise<IDBDatabase> {
	const request = indexedDB.open(name, DB_VERSION);

	const promise = new Promise<IDBDatabase>((resolve, reject) => {
		request.onerror = () => reject(request.error);
		request.onsuccess = () => {
			request.result.onversionchange = () => request.result.close();
			resolve(request.result);
		};
	});

	request.onupgradeneeded = () => {
		const db = request.result;

		for (const name of Array.from(db.objectStoreNames)) {
			if (!CONFIG[name]) db.deleteObjectStore(name);
		}

		for (const name in CONFIG) {
			const { id, indexes } = CONFIG[name];

			const store = db.objectStoreNames.contains(name)
				? request.transaction!.objectStore(name)
				: db.createObjectStore(name, { keyPath: id });

			for (const index of indexes ?? []) {
				if (store.indexNames.contains(index)) continue;

				store.createIndex(index, index);
			}
		}
	};

	return promise;
}

export function queryGetAll<T>(db: IDBDatabase, storeName: string): Promise<T[]> {
	return new Promise((resolve, reject) => {
		const tx = db.transaction(storeName, 'readonly');
		const request = tx.objectStore(storeName).getAll();

		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

export function queryGetByIndex<T>(
	db: IDBDatabase,
	storeName: string,
	indexName: string,
	key: IDBValidKey | IDBKeyRange,
): Promise<T[]> {
	return new Promise((resolve, reject) => {
		const tx = db.transaction(storeName, 'readonly');
		const request = tx.objectStore(storeName).index(indexName).getAll(key);

		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

export function queryGetByID<T>(
	db: IDBDatabase,
	storeName: string,
	id: IDBValidKey,
): Promise<T | undefined> {
	return new Promise((resolve, reject) => {
		const tx = db.transaction(storeName, 'readonly');
		const request = tx.objectStore(storeName).get(id);

		request.onsuccess = () => resolve(request.result ?? undefined);
		request.onerror = () => reject(request.error);
	});
}

export async function ingestStream(
	res: Response,
	db: IDBDatabase,
	setIngestTable: (table: string) => void,
): Promise<void> {
	let currentTable: string | null = null;
	let currentBatch: Record<string, unknown>[] = [];

	const flush = async (storeName: string | null, batch: Record<string, unknown>[]) => {
		if (!storeName || batch.length === 0) return;

		if (!CONFIG[storeName]) return;

		await flushToStore(db, storeName, batch);
	};

	if (!res.ok || !res.body) throw new Error('Analytics stream error: ' + res.status);

	const reader = res.body.getReader();
	const decoder = new TextDecoder();

	let buffer = '';

	while (true) {
		const { done, value } = await reader.read();

		if (done) break;

		buffer += decoder.decode(value, { stream: true });

		const lines = buffer.split('\n');

		buffer = lines.pop()!;

		for (const line of lines) {
			if (!line) continue;

			const record = JSON.parse(line) as Record<string, unknown>;
			const tableName = record._table as string;

			if (tableName !== currentTable) {
				await flush(currentTable, currentBatch);

				currentBatch = [];
				currentTable = tableName;

				setIngestTable(tableName);
			}

			delete record._table;

			convertDates(tableName, record);

			currentBatch.push(record);

			if (currentBatch.length >= BATCH_SIZE) {
				await flush(currentTable, currentBatch);

				currentBatch = [];
			}
		}
	}

	if (buffer) {
		const record = JSON.parse(buffer) as Record<string, unknown>;
		const tableName = record._table as string;

		if (tableName !== currentTable) {
			await flush(currentTable, currentBatch);

			currentBatch = [];
			currentTable = tableName;

			setIngestTable(tableName);
		}

		delete record._table;

		convertDates(tableName, record);

		currentBatch.push(record);
	}

	await flush(currentTable, currentBatch);
}

export async function clearDatabase(name: string) {
	localStorage.removeItem(`${SYNC}:${name}`);
	localStorage.removeItem(`${GROUPS}:${name}`);

	const db = await openDatabase(name);

	const stores: string[] = [].slice.call(db.objectStoreNames);

	const promises = stores.map((name) => {
		const tx = db.transaction(name, 'readwrite');
		const promise = idbTxComplete(tx);

		tx.objectStore(name).clear();

		return promise;
	});

	await Promise.all(promises);

	db.close();
}

function deleteDatabaseByName(name: string) {
	return new Promise<void>((resolve, reject) => {
		const request = indexedDB.deleteDatabase(name);

		request.onsuccess = () => resolve();
		request.onerror = () => reject(request.error);
		request.onblocked = () => resolve();
	});
}

export async function clearAllDatabases() {
	const databases = await indexedDB.databases();

	await Promise.all(
		databases
			.filter(({ name }) => name?.startsWith(`${PREFIX}:`))
			.map(({ name }) => deleteDatabaseByName(name!)),
	);

	Object.keys(localStorage)
		.filter((key) => key.startsWith(`${SYNC}:`) || key.startsWith(`${GROUPS}:`))
		.forEach((key) => localStorage.removeItem(key));
}

// Deletes every analytics database that does not belong to one of the given
// users in this workspace, so a device only retains snapshots for the accounts
// currently in the session. Also sweeps the legacy workspace-only database name
// and any orphaned sync/group markers left behind by earlier builds.
export async function pruneAnalyticsDatabases(workspaceAlias: string, userIDs: string[]) {
	const keep = new Set(userIDs.map((userID) => analyticsDatabaseName(workspaceAlias, userID)));

	// Unsupported in Firefox, where the caller keeps the distinct per-user names
	// but skips the cleanup.
	if (typeof indexedDB.databases === 'function') {
		const databases = await indexedDB.databases();

		const stale = databases
			.map(({ name }) => name)
			.filter((name): name is string => !!name && name.startsWith(`${PREFIX}:`) && !keep.has(name));

		await Promise.all(stale.map(deleteDatabaseByName));
	}

	for (const key of Object.keys(localStorage)) {
		const marker = [SYNC, GROUPS].find((prefix) => key.startsWith(`${prefix}:${PREFIX}:`));

		if (!marker) continue;

		if (!keep.has(key.slice(marker.length + 1))) localStorage.removeItem(key);
	}
}

async function flushToStore(
	db: IDBDatabase,
	storeName: string,
	records: Record<string, unknown>[],
) {
	if (records.length === 0) return;
	if (!db.objectStoreNames.contains(storeName)) return;

	const tx = db.transaction(storeName, 'readwrite');
	const store = tx.objectStore(storeName);

	const keyPath = CONFIG[storeName]?.id;

	records.forEach((record) => {
		try {
			store.put(record);
		} catch (err) {
			const key = keyPath ? record[keyPath] : undefined;

			console.error('failed to put', storeName, key, err);
		}
	});

	await idbTxComplete(tx);
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
}

function idbTxComplete(tx: IDBTransaction): Promise<void> {
	return new Promise((resolve, reject) => {
		tx.oncomplete = () => resolve();
		tx.onerror = () => reject(tx.error);
	});
}

export async function injectChildren(db: IDBDatabase): Promise<void> {
	for (const [storeName, config] of Object.entries(CONFIG)) {
		if (!config.parent) continue;

		const { store: parentStoreName, id: foreignKey } = config.parent;
		const parentID = CONFIG[parentStoreName].id!;

		const readTx = db.transaction(parentStoreName, 'readonly');
		const allParents = await idbRequest<Record<string, unknown>[]>(
			readTx.objectStore(parentStoreName).getAll(),
		);

		for (let i = 0; i < allParents.length; i += BATCH_SIZE) {
			const batch = allParents.slice(i, i + BATCH_SIZE);
			const tx = db.transaction([parentStoreName, storeName], 'readwrite');
			const childStore = tx.objectStore(storeName);
			const parentStore = tx.objectStore(parentStoreName);

			for (const parent of batch) {
				const key = parent[parentID] as string;
				const children = await idbRequest<Record<string, unknown>[]>(
					childStore.index(foreignKey).getAll(key),
				);
				parentStore.put({ ...parent, children });
			}

			await idbTxComplete(tx);
		}
	}
}

function convertDates(storeName: string, record: Record<string, unknown>) {
	const fields = CONFIG[storeName as keyof typeof CONFIG]?.dates;

	if (!fields) return;

	for (const field of fields) {
		const val = record[field];

		if (val === null || val === undefined) continue;

		const date = new Date(val as string | number);

		record[field] = Number.isNaN(date.getTime()) ? null : date;
	}
}
